/**
 * Sistema de Licencias para Risko Pulse
 *
 * Maneja la activación y validación de licencias premium
 */

import { db } from './firebase'
import { collection, doc, getDoc, setDoc, updateDoc, query, where, getDocs, serverTimestamp } from 'firebase/firestore'

export type LicenseType = 'free' | 'free_trial' | 'premium' | 'enterprise'

export type LicenseStatus = 'active' | 'expired' | 'revoked' | 'pending'

export interface License {
  id: string
  code: string // Código de licencia (ej: RISKO-PREMIUM-XXXX-XXXX-XXXX)
  type: LicenseType
  status: LicenseStatus
  userId?: string // Usuario que activó la licencia
  activatedAt?: number
  expiresAt?: number // null = sin expiración
  createdAt: number
  metadata?: {
    purchaseId?: string
    paymentMethod?: string
    duration?: number // Duración en días
  }
}

export interface UserLicense {
  userId: string
  licenseId: string
  type: LicenseType
  status: LicenseStatus
  activatedAt: number
  expiresAt?: number
  features: {
    unlimitedHistory: boolean
    telegramIntegration: boolean
    customNotifications: boolean
    prioritySupport: boolean
  }
}

/**
 * Genera un código de licencia único
 */
export function generateLicenseCode(type: LicenseType): string {
  const prefix = type.toUpperCase()
  const segments = []

  for (let i = 0; i < 3; i++) {
    const segment = Math.random().toString(36).substring(2, 6).toUpperCase()
    segments.push(segment)
  }

  return `RISKO-${prefix}-${segments.join('-')}`
}

/**
 * Valida formato de código de licencia
 */
export function validateLicenseFormat(code: string): boolean {
  const regex = /^RISKO-(FREE|PREMIUM|ENTERPRISE)-[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}$/
  return regex.test(code)
}

/**
 * Activa la prueba gratuita de 7 días para un usuario
 */
export async function activateFreeTrial(userId: string): Promise<{
  success: boolean
  message: string
  license?: UserLicense
}> {
  try {
    // Verificar si ya tiene una licencia activa
    const existingLicense = await getUserLicense(userId)
    
    if (existingLicense && existingLicense.status === 'active') {
      return {
        success: false,
        message: 'Ya tienes una licencia activa'
      }
    }

    // Verificar si ya usó la prueba gratuita (buscando en user_licenses con type free_trial)
    if (existingLicense && existingLicense.type === 'free_trial') {
      return {
        success: false,
        message: 'Ya has utilizado tu prueba gratuita de 7 días'
      }
    }

    // Calcular fechas
    const activatedAt = Date.now()
    const trialDurationDays = 7
    const expiresAt = activatedAt + (trialDurationDays * 24 * 60 * 60 * 1000)

    // Crear licencia de prueba gratuita
    const trialLicense: UserLicense = {
      userId,
      licenseId: `trial-${userId}-${Date.now()}`,
      type: 'free_trial',
      status: 'active',
      activatedAt,
      expiresAt,
      features: getLicenseFeatures('free_trial')
    }

    if (!db) {
      return {
        success: false,
        message: 'Firestore no está inicializado'
      }
    }
    // Guardar en user_licenses
    await setDoc(doc(db, 'user_licenses', userId), trialLicense)

    // Actualizar usuario con tipo de licencia
    await updateDoc(doc(db, 'users', userId), {
      plan: 'free_trial',
      trialStartDate: activatedAt,
      trialEndDate: expiresAt,
      updatedAt: serverTimestamp()
    })

    return {
      success: true,
      message: 'Prueba gratuita de 7 días activada exitosamente',
      license: trialLicense
    }

  } catch (error) {
    console.error('Error activating free trial:', error)
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Error al activar prueba gratuita'
    }
  }
}

/**
 * Activa una licencia para un usuario
 */
export async function activateLicense(userId: string, licenseCode: string): Promise<{
  success: boolean
  message: string
  license?: UserLicense
}> {
  try {
    // 1. Validar formato
    if (!validateLicenseFormat(licenseCode)) {
      return {
        success: false,
        message: 'Código de licencia inválido. Formato correcto: RISKO-PREMIUM-XXXX-XXXX-XXXX'
      }
    }

    if (!db) {
      return {
        success: false,
        message: 'Firestore no está inicializado'
      }
    }
    // 2. Buscar licencia en Firestore
    const licensesRef = collection(db, 'licenses')
    const q = query(licensesRef, where('code', '==', licenseCode))
    const snapshot = await getDocs(q)

    if (snapshot.empty) {
      return {
        success: false,
        message: 'Código de licencia no encontrado. Verifica que sea correcto.'
      }
    }

    const licenseDoc = snapshot.docs[0]
    const license = licenseDoc.data() as License

    // 3. Verificar que la licencia no esté ya activada
    if (license.userId && license.userId !== userId) {
      return {
        success: false,
        message: 'Esta licencia ya está activada por otro usuario.'
      }
    }

    // Idempotent: same user, already active → return success
    if (license.userId === userId && license.status === 'active') {
      const userLicense = await getUserLicense(userId)
      return {
        success: true,
        message: 'Licencia ya activa',
        license: userLicense ?? undefined
      }
    }

    // 4. Verificar que la licencia no esté expirada o revocada
    if (license.status === 'expired') {
      return {
        success: false,
        message: 'Esta licencia ha expirado. Contacta a soporte.'
      }
    }

    if (license.status === 'revoked') {
      return {
        success: false,
        message: 'Esta licencia ha sido revocada. Contacta a soporte.'
      }
    }

    // 5. Calcular fecha de expiración
    const activatedAt = Date.now()
    const duration = license.metadata?.duration || null // null = sin expiración
    const expiresAt = duration ? activatedAt + (duration * 24 * 60 * 60 * 1000) : undefined

    // 6. Activar licencia
    await updateDoc(doc(db, 'licenses', licenseDoc.id), {
      userId,
      status: 'active',
      activatedAt,
      expiresAt
    })

    // 7. Crear registro en user_licenses
    const userLicense: UserLicense = {
      userId,
      licenseId: licenseDoc.id,
      type: license.type,
      status: 'active',
      activatedAt,
      expiresAt,
      features: getLicenseFeatures(license.type)
    }

    await setDoc(doc(db, 'user_licenses', userId), userLicense)

    // 8. Actualizar usuario con tipo de licencia
    await updateDoc(doc(db, 'users', userId), {
      licenseType: license.type,
      licenseStatus: 'active',
      licenseActivatedAt: activatedAt,
      updatedAt: activatedAt
    })

    return {
      success: true,
      message: `¡Licencia ${license.type.toUpperCase()} activada exitosamente!`,
      license: userLicense
    }

  } catch (error) {
    console.error('Error activating license:', error)
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Error al activar licencia'
    }
  }
}

/**
 * Obtiene las features según el tipo de licencia
 */
export function getLicenseFeatures(type: LicenseType): UserLicense['features'] {
  switch (type) {
    case 'free':
      return {
        unlimitedHistory: false,
        telegramIntegration: false,
        customNotifications: false,
        prioritySupport: false
      }

    case 'free_trial':
      // Prueba gratuita de 7 días - acceso limitado al canal público
      return {
        unlimitedHistory: false,
        telegramIntegration: false, // Solo acceso al canal público, no chat privado
        customNotifications: false,
        prioritySupport: false
      }

    case 'premium':
      return {
        unlimitedHistory: true,
        telegramIntegration: true,
        customNotifications: true,
        prioritySupport: false
      }

    case 'enterprise':
      return {
        unlimitedHistory: true,
        telegramIntegration: true,
        customNotifications: true,
        prioritySupport: true
      }

    default:
      return {
        unlimitedHistory: false,
        telegramIntegration: false,
        customNotifications: false,
        prioritySupport: false
      }
  }
}

/**
 * Verifica si un usuario tiene una licencia activa
 */
export async function getUserLicense(userId: string): Promise<UserLicense | null> {
  try {
    if (!db) {
      return null
    }
    const docRef = doc(db, 'user_licenses', userId)
    const docSnap = await getDoc(docRef)

    if (!docSnap.exists()) {
      return null
    }

    const license = docSnap.data() as UserLicense

    // Verificar si está expirada
    if (license.expiresAt && license.expiresAt < Date.now()) {
      // Actualizar estado a expirado
      await updateDoc(docRef, { status: 'expired' })

      return {
        ...license,
        status: 'expired'
      }
    }

    return license

  } catch (error) {
    console.error('Error getting user license:', error)
    return null
  }
}

/**
 * Verifica si un usuario tiene acceso a una feature
 */
export async function hasFeatureAccess(userId: string, feature: keyof UserLicense['features']): Promise<boolean> {
  const license = await getUserLicense(userId)

  if (!license || license.status !== 'active') {
    return false
  }

  return license.features[feature]
}

/**
 * Revoca una licencia
 */
export async function revokeLicense(licenseId: string, reason?: string): Promise<boolean> {
  try {
    if (!db) {
      return false
    }
    await updateDoc(doc(db, 'licenses', licenseId), {
      status: 'revoked',
      revokedAt: Date.now(),
      revokeReason: reason || 'No reason provided'
    })

    return true
  } catch (error) {
    console.error('Error revoking license:', error)
    return false
  }
}

/**
 * Crea una nueva licencia (admin only)
 */
export async function createLicense(
  type: LicenseType,
  duration?: number, // Duración en días (null = sin expiración)
  metadata?: License['metadata']
): Promise<License> {
  if (!db) {
    throw new Error('Firestore no está inicializado')
  }
  const code = generateLicenseCode(type)

  const license: License = {
    id: `license-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
    code,
    type,
    status: 'pending',
    createdAt: Date.now(),
    metadata: {
      ...metadata,
      duration
    }
  }

  await setDoc(doc(db, 'licenses', license.id), license)

  return license
}
